import { useEffect, useState } from 'react';
import * as THREE from 'three';
import { PlayerInfo } from '@/types/shot.types';

interface PlayerLabelProps {
  playerInfo: PlayerInfo;
  color: number;
  position: THREE.Vector3;
  camera: THREE.PerspectiveCamera;
}

/**
 * PlayerLabel component
 * Displays player name and team logo as an HTML overlay
 * Positioned using world-to-screen projection
 */
export function PlayerLabel({
  playerInfo,
  color,
  position,
  camera,
}: PlayerLabelProps) {
  const [screenPos, setScreenPos] = useState({ x: 0, y: 0, visible: true });

  useEffect(() => {
    // Project 3D position to screen coordinates
    const vector = position.clone();
    vector.project(camera);

    const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
    const y = (vector.y * -0.5 + 0.5) * window.innerHeight;
    const visible = vector.z <= 1; // Hide if behind camera

    setScreenPos({ x, y, visible });
  }, [position, camera]);

  if (!screenPos.visible) {
    return null;
  }

  // Convert color number to hex string
  const colorHex = `#${color.toString(16).padStart(6, '0')}`;

  return (
    <div
      style={{
        position: 'absolute',
        left: `${screenPos.x}px`,
        top: `${screenPos.y}px`,
        transform: 'translate(-50%, -100%)',
        marginTop: '-10px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 6px 2px 2px',
        backgroundColor: colorHex,
        color: '#ffffff',
        fontFamily: 'Arial, sans-serif',
        fontSize: '14px',
        fontWeight: 'bold',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}
    >
      {playerInfo.teamLogoUrl ? (
        <img
          src={playerInfo.teamLogoUrl}
          alt={playerInfo.teamName || 'Team logo'}
          style={{
            width: '30px',
            height: '30px',
            objectFit: 'contain',
          }}
        />
      ) : (
        <div
          style={{
            width: '20px',
            height: '20px',
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
          }}
        >
          ⛳
        </div>
      )}
      <span>{playerInfo.name}</span>
    </div>
  );
}
